import {
  CopyIcon,
  DownloadSimpleIcon,
  EyeIcon,
  MagnifyingGlassIcon,
  PlusIcon,
} from '@phosphor-icons/react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import { Page } from '@/components/layout/page'
import { PageHeader } from '@/components/layout/page-header'
import { TableGroup } from '@/components/layout/table-group'
import { Button } from '@/components/ui/button'
import { Icon } from '@/components/ui/icon'
import { Select } from '@/components/ui/select'
import { RowAction } from '@/components/ui/table'
import { TextInput } from '@/components/ui/text-input'
import { ToggleSwitch } from '@/components/ui/toggle-switch'
import { popupFactory, routerFactory, toastFactory } from '@/factories'
import { useDesignVersionToggle, useFilters, useSearchDraft, useSearchParams } from '@/hooks'
import { selectBuildings, selectBuildingsLoaded } from '@/reducers/buildingSelectorReducer'
import { Building } from '@/reducers/types'
import { getApiErrorMessage } from '@/utils/error'

import { CreatedDateRangePicker } from '../shared/created-date-range-picker'
import { exportCsv } from '../shared/export-csv'
import { filterByDateRange, paginateItems } from '../shared/person-table.utils'
import { PlaceFormModal } from './place-form-modal'
import {
  getFeatureFilterOptions,
  getPlacesTableColumns,
  getPmsFilterOptions,
  getStatusFilterOptions,
  PLACES_TABLE_ID,
  placesTableFiltersSchema,
  placesTableInitialFilters,
} from './places.constants'
import { PlacePmsType, PMS_NONE } from './places.pms'
import { PlaceStatusFilter } from './places.status-filter'
import * as S from './places.styled'
import {
  filterPlaces,
  filterPlacesByFeature,
  filterPlacesByPms,
  filterPlacesByStatus,
  filterPlacesByTags,
  getPlacesCsvFields,
  getPlaceTags,
  sortPlaces,
} from './places.utils'

export const PlacesPage = () => {
  const { t } = useTranslation(['organisation'])
  const { navigate } = routerFactory.useRouter()
  const buildings = useSelector(selectBuildings)
  const buildingsLoaded = useSelector(selectBuildingsLoaded)

  const {
    canToggle,
    redirect,
    toggle: handleUIToggle,
  } = useDesignVersionToggle({
    group: 'places',
    screen: 'Places',
    to: { path: '/dashboard/organisation/places/legacy' },
    version: 'new',
  })

  const searchParams = useSearchParams({
    initialState: placesTableInitialFilters,
    schema: placesTableFiltersSchema,
  })

  const tableFilters = useFilters({
    controlledState: searchParams.parsedQuery,
    initialState: placesTableInitialFilters,
    onFiltersChange: searchParams.writeParams,
  })

  const { feature, fromDate, limit, pms, search, skip, sortBy, sortOrder, status, tags, toDate } =
    tableFilters.filters

  const [searchDraft, handleSearchChange] = useSearchDraft(search, (value) =>
    tableFilters.setFilter('search', value),
  )

  const statusOptions = useMemo(() => getStatusFilterOptions(t), [t])
  const pmsOptions = useMemo(() => getPmsFilterOptions(t), [t])
  const featureOptions = useMemo(() => getFeatureFilterOptions(t), [t])
  const tagOptions = useMemo(
    () => getPlaceTags(buildings).map((tag) => ({ label: tag, value: tag })),
    [buildings],
  )

  const filteredPlaces = useMemo(
    () =>
      sortPlaces(
        filterPlaces(
          filterPlacesByTags(
            filterPlacesByFeature(
              filterPlacesByPms(
                filterPlacesByStatus(
                  filterByDateRange(buildings, fromDate, toDate, (building) =>
                    new Date(building.createdAt ?? 0).getTime(),
                  ),
                  status,
                ),
                pms as Array<PlacePmsType | typeof PMS_NONE> | undefined,
              ),
              feature,
            ),
            tags,
          ),
          search,
        ),
        sortBy,
        sortOrder,
      ),
    [buildings, fromDate, toDate, status, pms, feature, tags, search, sortBy, sortOrder],
  )

  const pagePlaces = useMemo(
    () => paginateItems(filteredPlaces, skip, limit),
    [filteredPlaces, skip, limit],
  )

  const columns = useMemo(() => getPlacesTableColumns(t), [t])

  const [isExporting, setIsExporting] = useState(false)

  const handleExportCsv = async () => {
    setIsExporting(true)
    try {
      await exportCsv({ data: filteredPlaces, fields: getPlacesCsvFields(t), page: 'places' })
    } catch (error) {
      toastFactory.error(getApiErrorMessage(error))
    } finally {
      setIsExporting(false)
    }
  }

  const handleCopyCode = async (building: Building) => {
    try {
      await navigator.clipboard.writeText(building.code ?? '')
      toastFactory.success(t('organisation:places.table.toasts.codeCopied'))
    } catch (error) {
      toastFactory.error(getApiErrorMessage(error))
    }
  }

  const handleSort = (nextSortBy: string | null, nextSortOrder: 'asc' | 'desc' | null) => {
    tableFilters.setFilters({
      sortBy: (nextSortBy as typeof sortBy) ?? undefined,
      sortOrder: nextSortOrder ?? undefined,
    })
  }

  const rowActions: RowAction<Building>[] = useMemo(
    () => [
      {
        icon: EyeIcon,
        label: t('organisation:places.table.rowActions.details'),
        onClick: (building) =>
          navigate({
            params: { buildingId: String(building.id) },
            path: '/dashboard/organisation/places/:buildingId',
          }),
        primary: true,
      },
      {
        icon: CopyIcon,
        label: t('organisation:places.table.rowActions.copyCode'),
        onClick: handleCopyCode,
      },
    ],
    [t, navigate],
  )

  if (redirect) return redirect

  return (
    <Page>
      <PageHeader
        title={t('organisation:places.table.title')}
        actions={
          <>
            {canToggle && (
              <ToggleSwitch
                falsyLabel={t('organisation:places.table.designToggle.old')}
                truthyLabel={t('organisation:places.table.designToggle.new')}
                checked={true}
                onChange={handleUIToggle}
              />
            )}

            <Button
              size="small"
              use="primary"
              onClick={() => popupFactory.open(PlaceFormModal, {})}
            >
              {t('organisation:places.table.createNew')}
              <Icon icon={PlusIcon} />
            </Button>
          </>
        }
      />

      <TableGroup
        columns={columns}
        data={pagePlaces}
        emptyNoFilters={{
          description: t('organisation:places.table.empty.noFilters.description'),
          title: t('organisation:places.table.empty.noFilters.title'),
        }}
        emptyWithFilters={{
          description: t('organisation:places.table.empty.withFilters.description'),
          title: t('organisation:places.table.empty.withFilters.title'),
        }}
        filters={
          <>
            <Select
              options={statusOptions}
              placeholder={t('organisation:places.table.filters.status.placeholder')}
              size="small"
              value={status ?? PlaceStatusFilter.all}
              onChange={(value) => tableFilters.setFilter('status', value)}
              onReset={() => tableFilters.setFilter('status', PlaceStatusFilter.all)}
            />
            <Select
              type="multi"
              options={pmsOptions}
              placeholder={t('organisation:places.table.filters.pms.placeholder')}
              size="small"
              value={pms ?? []}
              onChange={(values) =>
                tableFilters.setFilter('pms', values.length ? values : undefined)
              }
              onReset={() => tableFilters.setFilter('pms', undefined)}
            />
            <Select
              searchable
              type="multi"
              options={tagOptions}
              placeholder={t('organisation:places.table.filters.tags.placeholder')}
              size="small"
              value={tags ?? []}
              onChange={(values) =>
                tableFilters.setFilter('tags', values.length ? values : undefined)
              }
              onReset={() => tableFilters.setFilter('tags', undefined)}
            />
            <S.FeatureFilter>
              <Select
                searchable
                type="multi"
                options={featureOptions}
                placeholder={t('organisation:places.table.filters.feature.placeholder')}
                size="small"
                value={feature ?? []}
                onChange={(values) =>
                  tableFilters.setFilter('feature', values.length ? values : undefined)
                }
                onReset={() => tableFilters.setFilter('feature', undefined)}
              />
            </S.FeatureFilter>
          </>
        }
        getRowKey={(building) => building.id}
        id={PLACES_TABLE_ID}
        loading={!buildingsLoaded}
        navigation={{
          actions: (
            <Button
              onlyBox
              disabled={!filteredPlaces.length}
              loading={isExporting}
              size="small"
              title={t('organisation:places.table.exportCsv')}
              use="secondary"
              onClick={handleExportCsv}
            >
              <Icon icon={DownloadSimpleIcon} />
            </Button>
          ),
          datePicker: (
            <CreatedDateRangePicker
              fromDate={fromDate}
              toDate={toDate}
              onChange={(range) => tableFilters.setFilters(range)}
            />
          ),
          search: (
            <TextInput
              clearable
              icon={MagnifyingGlassIcon}
              name="search"
              placeholder={t('organisation:places.table.search.placeholder')}
              size="small"
              value={searchDraft}
              onChange={(e) => handleSearchChange(e.target.value)}
            />
          ),
        }}
        pagination={{ paginationFilters: tableFilters, total: filteredPlaces.length }}
        rowActions={rowActions}
        sortBy={sortBy ?? null}
        sortOrder={sortOrder ?? null}
        tableFilters={tableFilters}
        onSort={handleSort}
      />
    </Page>
  )
}
